import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {Alert, ConfigProvider, Form, Input, Result, Skeleton, Tabs, Typography} from "antd";
import {containsNonFiniteNumbers} from "../userFacingErrorUtils";
import {buildPublicRegistrationTheme} from "../Theme/publicRegistrationTheme";
import {LanguageSwitch, PublicShell} from "../Components/ui";
import {startPublicRegistrationHeightReporter} from "./publicRegistrationHeightReporter";
import {BulkImportNotice, BulkRegistrationSection} from "./publicRegistration/BulkRegistrationSection";
import {describeBulkCsvImport, parseBulkRegistrationCsv} from "./publicRegistration/bulkRegistrationCsv";
import {SubmitErrorAlert} from "./publicRegistration/FormFeedback";
import {EventMaterials} from "./publicRegistration/EventMaterials";
import {EventBand, RegistrationBrandLockup} from "./publicRegistration/RegistrationHeader";
import {RegistrationResult, ResendConfirmationNotice} from "./publicRegistration/RegistrationResult";
import {SingleRegistrationForm} from "./publicRegistration/SingleRegistrationForm";
import {useRegistrationFormState} from "./publicRegistration/useRegistrationFormState";
import {
    fetchPublicEventForm,
    resendPublicRegistrationConfirmation,
    submitPublicRegistration,
    uploadParticipantTypeProofFile
} from "./publicRegistration/publicRegistrationApi";
import {
    buildInitialFormValues,
    buildPublicEventAssetLinks,
    detectInsideFrame,
    formatBulkSubmitSummary,
    formatEventDateRange,
    getPublicEventName,
    getPublicRegistrationUnavailableReason,
    normalizePublicRegistrationLanguage,
    shouldUseEmbeddedLayout,
    trimValue
} from "./publicRegistration/publicRegistrationModel";
import {
    buildPublicRegistrationDraftStorageKey,
    clearPublicRegistrationDraft,
    readPublicRegistrationDraft,
    writePublicRegistrationDraft
} from "./publicRegistration/registrationDraft";
import {
    buildPublicRegistrationBulkSubmitPayloads,
    buildPublicRegistrationSubmitPayload
} from "./publicRegistration/registrationPayload";
import {
    buildRegistrationCopy,
    RegistrationCopyProvider,
    usePublicRegistrationLabels
} from "./publicRegistration/registrationCopy";
import {localizeSubmitErrorMessage} from "./publicRegistration/submitErrors";
import {EventOfferLineSourceTypeValue} from "./publicRegistration/types";
import type {
    PublicEventFormResponse,
    PublicRegistrationBulkSubmissionResult,
    PublicRegistrationFormValues,
    PublicRegistrationLanguage,
    PublicRegistrationMode,
    PublicRegistrationResponse,
    PublicRegistrationSubmissionContext,
    PublicRegistrationSubmitPayload
} from "./publicRegistration/types";

const PublicRegistrationPage: FC = () => {
    const {eventIdentifier} = useParams<{ eventIdentifier: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const language = normalizePublicRegistrationLanguage(searchParams.get("lang"));
    const labels = usePublicRegistrationLabels(language);
    const copy = useMemo(() => buildRegistrationCopy(labels, language), [labels, language]);
    const [registrationForm] = Form.useForm<PublicRegistrationFormValues>();
    const watchedValues = Form.useWatch([], registrationForm) as PublicRegistrationFormValues | undefined;
    const [registrationMode, setRegistrationMode] = useState<PublicRegistrationMode>("single");
    const submitErrorAlertRef = useRef<HTMLDivElement | null>(null);
    const [eventForm, setEventForm] = useState<PublicEventFormResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [bulkImportNotice, setBulkImportNotice] = useState<BulkImportNotice | null>(null);
    const [bulkValidationNotice, setBulkValidationNotice] = useState<string | null>(null);
    const [bulkSubmissionResults, setBulkSubmissionResults] = useState<PublicRegistrationBulkSubmissionResult[]>([]);
    const [submission, setSubmission] = useState<PublicRegistrationResponse | null>(null);
    const [submissionContext, setSubmissionContext] = useState<PublicRegistrationSubmissionContext | null>(null);
    const [participantTypeProofUploading, setParticipantTypeProofUploading] = useState(false);
    const [resendingConfirmation, setResendingConfirmation] = useState(false);
    const [resendConfirmationNotice, setResendConfirmationNotice] = useState<ResendConfirmationNotice | null>(null);
    const embedded = useMemo(() => shouldUseEmbeddedLayout(searchParams, detectInsideFrame()), [searchParams]);
    const pageRef = useRef<HTMLElement>(null);
    useEffect(
        () => startPublicRegistrationHeightReporter({embedded, element: pageRef.current}),
        [embedded]);
    const draftStorageKey = useMemo(
        () => eventIdentifier ? buildPublicRegistrationDraftStorageKey(eventIdentifier) : undefined,
        [eventIdentifier]);
    const saveCurrentDraft = useCallback((
        values: PublicRegistrationFormValues,
        mode: PublicRegistrationMode = registrationMode
    ) => {
        writePublicRegistrationDraft(draftStorageKey, values, mode);
    }, [draftStorageKey, registrationMode]);
    const focusSubmitErrorAlert = useCallback(() => {
        window.setTimeout(() => {
            const alert = submitErrorAlertRef.current;
            if (!alert) {
                return;
            }

            if (typeof alert.scrollIntoView === "function") {
                alert.scrollIntoView({behavior: "smooth", block: "center"});
            }
            alert.focus();
        }, 0);
    }, []);

    const loadForm = useCallback(async (signal?: AbortSignal) => {
        if (!eventIdentifier) {
            setError(labels.missingCode);
            setEventForm(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = await fetchPublicEventForm(eventIdentifier, signal);
            setEventForm(payload);
            setSubmission(null);
            setSubmissionContext(null);
        } catch (requestError) {
            if ((requestError as DOMException).name !== "AbortError") {
                setError(labels.loadError);
                setEventForm(null);
            }
        } finally {
            setLoading(false);
        }
    }, [eventIdentifier, labels.loadError, labels.missingCode]);

    useEffect(() => {
        const abortController = new AbortController();
        loadForm(abortController.signal);

        return () => abortController.abort();
    }, [loadForm]);

    useEffect(() => {
        if (eventForm) {
            const draft = readPublicRegistrationDraft(draftStorageKey, eventForm);
            registrationForm.setFieldsValue(draft?.values ?? buildInitialFormValues(eventForm));
            setRegistrationMode(draft?.mode ?? "single");
        }
    }, [draftStorageKey, eventForm, registrationForm]);

    const onLanguageChange = (nextLanguage: PublicRegistrationLanguage) => {
        saveCurrentDraft(registrationForm.getFieldsValue(true), registrationMode);
        const next = new URLSearchParams(searchParams);
        next.set("lang", nextLanguage);
        setSearchParams(next, {replace: true});
    };

    const uploadParticipantTypeProof = async (file: File) => {
        if (!eventIdentifier || participantTypeProofUploading || submitting) {
            return;
        }

        setParticipantTypeProofUploading(true);
        setSubmitError(null);

        try {
            const uploaded = await uploadParticipantTypeProofFile(
                eventIdentifier,
                file,
                labels.participantTypeProofUploadFailed);
            if (!Number.isInteger(uploaded?.id) || uploaded.id <= 0) {
                throw new Error(labels.participantTypeProofUploadFailed);
            }
            const nextValues = {
                ...registrationForm.getFieldsValue(true),
                participantTypeProofFileId: uploaded.id,
                participantTypeProofFileName: uploaded.fileName ?? file.name
            };
            registrationForm.setFieldsValue(nextValues);
            saveCurrentDraft(nextValues, registrationMode);
        } catch (uploadError) {
            const message = uploadError instanceof Error
                ? localizeSubmitErrorMessage(uploadError.message, {
                    ...labels,
                    submitError: labels.participantTypeProofUploadFailed
                }, language)
                : labels.participantTypeProofUploadFailed;
            setSubmitError(message);
        } finally {
            setParticipantTypeProofUploading(false);
        }
    };

    const {
        participationMethods,
        accommodationMethods,
        selectedRoom,
        reserveAccommodation,
        requiresParticipationFee,
        selectedParticipationFee,
        availableRegistrationOptions,
        selectedParticipationFeeRequiresProof,
        hasParticipationOfferItems,
        payerType,
        accommodationEstimate
    } = useRegistrationFormState(eventForm, registrationForm, watchedValues, registrationMode);

    const publicEventName = eventForm ? getPublicEventName(eventForm, language) : eventIdentifier || labels.pageTitle;
    const registrationUnavailableReason = eventForm
        ? getPublicRegistrationUnavailableReason(eventForm)
        : null;
    const publicTheme = useMemo(() => buildPublicRegistrationTheme(eventForm), [eventForm]);
    const eventAssetLinks = useMemo(
        () => eventForm ? buildPublicEventAssetLinks(eventForm, labels) : [],
        [eventForm, labels]);
    const localizeSubmissionItem = (item: PublicRegistrationSubmitPayload["items"][number]) => {
        if (item.sourceType === EventOfferLineSourceTypeValue.EventFeePrice) {
            const fee = eventForm?.participationFees?.find(candidate => candidate.feePriceId === item.sourceId);
            if (fee) {
                return copy.participationFeeName(fee);
            }
        }

        if (item.sourceType === EventOfferLineSourceTypeValue.EventOption) {
            const option = eventForm?.registrationOptions?.find(candidate => candidate.id === item.sourceId);
            if (option) {
                return copy.registrationOptionName(option);
            }
        }

        return item.description;
    };

    const resetToFreshRegistration = (form: PublicEventFormResponse) => {
        registrationForm.resetFields();
        registrationForm.setFieldsValue(buildInitialFormValues(form));
    };

    const submitRegistration = async (values: PublicRegistrationFormValues) => {
        if (!eventIdentifier || !eventForm || participantTypeProofUploading) {
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            const payload = buildPublicRegistrationSubmitPayload(values, eventForm);
            if (containsNonFiniteNumbers(payload)) {
                throw new Error(labels.invalidValuesError);
            }

            const nextSubmissionContext = {
                email: payload.email,
                participantName: [payload.firstName, payload.lastName].filter(Boolean).join(" "),
                summaryItems: payload.items.map(item => ({
                    label: localizeSubmissionItem(item),
                    amount: item.quantity * item.unitPrice
                }))
            };
            const responsePayload = await submitPublicRegistration(eventIdentifier, payload, labels.submitError);
            clearPublicRegistrationDraft(draftStorageKey);
            setSubmissionContext(nextSubmissionContext);
            setSubmission(responsePayload);
            setResendConfirmationNotice(null);
            resetToFreshRegistration(eventForm);
        } catch (submitException) {
            const message = submitException instanceof Error
                ? submitException.message
                : labels.submitError;
            setSubmitError(localizeSubmitErrorMessage(message, labels, language));
            focusSubmitErrorAlert();
        } finally {
            setSubmitting(false);
        }
    };

    const handleRegistrationFinishFailed = ({
        errorFields
    }: {
        errorFields: Array<{name: Array<string | number>}>;
    }) => {
        setSubmitError(labels.validationErrorSummary);

        const firstInvalidFieldName = errorFields[0]?.name;
        if (!firstInvalidFieldName) {
            return;
        }

        registrationForm.scrollToField(firstInvalidFieldName, {
            behavior: "smooth",
            block: "center"
        });

        const fieldInstance = registrationForm.getFieldInstance(firstInvalidFieldName) as {
            focus?: () => void;
            input?: {focus?: () => void};
        } | undefined;
        (fieldInstance?.input ?? fieldInstance)?.focus?.();
    };

    const resendConfirmationEmail = async () => {
        if (!eventIdentifier || !submission || !submissionContext?.email) {
            return;
        }

        setResendingConfirmation(true);
        setResendConfirmationNotice(null);

        try {
            const responsePayload = await resendPublicRegistrationConfirmation(
                eventIdentifier,
                submission.registrationId,
                submissionContext.email,
                labels.confirmationResendFailed);
            setSubmission(previous => previous ? {...previous, ...responsePayload} : responsePayload);
            setResendConfirmationNotice({
                type: "success",
                message: labels.confirmationResent
            });
        } catch (resendError) {
            setResendConfirmationNotice({
                type: "error",
                message: resendError instanceof Error ? resendError.message : labels.confirmationResendFailed
            });
        } finally {
            setResendingConfirmation(false);
        }
    };

    const importBulkRegistrationCsvFile = async (file?: File | null) => {
        if (!file || !eventForm) {
            return;
        }

        try {
            const outcome = describeBulkCsvImport(
                parseBulkRegistrationCsv(await file.text(), eventForm.template?.fields, eventForm.registrationOptions),
                labels);

            if (outcome.rows) {
                registrationForm.setFieldsValue({bulkRows: outcome.rows});
                saveCurrentDraft({...registrationForm.getFieldsValue(true), bulkRows: outcome.rows}, registrationMode);
                setSubmitError(null);
                setBulkValidationNotice(null);
                setBulkSubmissionResults([]);
            }

            setBulkImportNotice(outcome.notice);
        } catch {
            setBulkImportNotice({
                type: "error",
                message: labels.bulkImportFailed
            });
        }
    };

    // Returns the blocking error for group entry, if any, before field validation runs.
    const bulkPreconditionError = (): string | null => {
        if (selectedParticipationFeeRequiresProof) {
            return labels.bulkProofNotSupported;
        }

        if (hasParticipationOfferItems && participationMethods.length === 0) {
            return labels.missingPaymentMethods;
        }

        return null;
    };

    const resetBulkFeedback = () => {
        setSubmitError(null);
        setBulkValidationNotice(null);
        setBulkSubmissionResults([]);
    };

    const validateBulkRegistration = async () => {
        resetBulkFeedback();

        const preconditionError = bulkPreconditionError();
        if (preconditionError) {
            setSubmitError(preconditionError);
            return;
        }

        try {
            await registrationForm.validateFields();
            setBulkValidationNotice(labels.bulkValidationPassed);
        } catch {
            setSubmitError(labels.bulkValidationFailed);
        }
    };

    const submitBulkRegistration = async () => {
        if (!eventIdentifier || !eventForm) {
            return;
        }

        setSubmitting(true);
        resetBulkFeedback();

        const preconditionError = bulkPreconditionError();
        if (preconditionError) {
            setSubmitError(preconditionError);
            setSubmitting(false);
            return;
        }

        try {
            const values = await registrationForm.validateFields();
            const payloads = buildPublicRegistrationBulkSubmitPayloads(values, eventForm);
            if (payloads.length === 0) {
                setSubmitError(labels.bulkValidationFailed);
                return;
            }

            if (containsNonFiniteNumbers(payloads)) {
                setSubmitError(labels.invalidValuesError);
                return;
            }

            const results: PublicRegistrationBulkSubmissionResult[] = [];

            for (const [index, payload] of payloads.entries()) {
                const participantName = `${payload.firstName} ${payload.lastName}`.trim();

                try {
                    const responsePayload = await submitPublicRegistration(eventIdentifier, payload, labels.submitError);
                    results.push({
                        rowNumber: index + 1,
                        participantName,
                        email: payload.email,
                        status: "accepted",
                        verificationTokenExpiresAtUtc: responsePayload.verificationTokenExpiresAtUtc
                    });
                } catch (rowSubmitException) {
                    results.push({
                        rowNumber: index + 1,
                        participantName,
                        email: payload.email,
                        status: "rejected",
                        error: rowSubmitException instanceof Error
                            ? localizeSubmitErrorMessage(rowSubmitException.message, labels, language)
                            : labels.submitError
                    });
                }
            }

            setBulkSubmissionResults(results);

            const accepted = results.filter(result => result.status === "accepted").length;
            const rejected = results.length - accepted;

            if (rejected === 0) {
                clearPublicRegistrationDraft(draftStorageKey);
                setBulkValidationNotice(formatBulkSubmitSummary(
                    hasParticipationOfferItems ? labels.bulkSubmitSuccess : labels.bulkSubmitSuccessNoPayment,
                    accepted,
                    rejected));
            } else {
                setSubmitError(formatBulkSubmitSummary(
                    accepted === 0 ? labels.bulkSubmitFailed : labels.bulkSubmitPartialFailure,
                    accepted,
                    rejected));
            }
        } catch (bulkException) {
            setSubmitError(bulkException instanceof Error
                ? localizeSubmitErrorMessage(bulkException.message, labels, language)
                : labels.bulkValidationFailed);
        } finally {
            setSubmitting(false);
        }
    };

    const setRegistrationModeState = (nextMode: PublicRegistrationMode) => {
        setRegistrationMode(nextMode);
        resetBulkFeedback();
        saveCurrentDraft(registrationForm.getFieldsValue(true), nextMode);
    };

    const submitErrorAlert = <SubmitErrorAlert ref={submitErrorAlertRef} message={submitError}/>;

    const renderRegistration = (form: PublicEventFormResponse) => {
        if (registrationUnavailableReason) {
            return (
                <Result
                    status="error"
                    title={registrationUnavailableReason === "form-inactive"
                        ? labels.registrationFormInactiveTitle
                        : labels.registrationUnavailableTitle}
                    subTitle={{
                        archived: labels.registrationUnavailableArchived,
                        completed: labels.registrationUnavailableCompleted,
                        ended: labels.registrationUnavailableEnded,
                        "form-inactive": labels.registrationUnavailableFormInactive
                    }[registrationUnavailableReason]}
                />
            );
        }

        return (
            <>
                <EventBand dateRange={formatEventDateRange(form, language)} place={trimValue(form.eventPlace)}/>
                <EventMaterials links={eventAssetLinks}/>

                {submission ? (
                    <RegistrationResult
                        submission={submission}
                        context={submissionContext}
                        resending={resendingConfirmation}
                        resendNotice={resendConfirmationNotice}
                        onResendConfirmation={resendConfirmationEmail}
                        onStartAnother={() => {
                            clearPublicRegistrationDraft(draftStorageKey);
                            setSubmission(null);
                            setSubmissionContext(null);
                            setResendConfirmationNotice(null);
                            resetToFreshRegistration(form);
                        }}
                    />
                ) : (
                    <>
                        <nav className="public-registration-mode-bar" aria-label={labels.registrationMode}>
                            <Tabs
                                type="card"
                                activeKey={registrationMode}
                                onChange={(key) => setRegistrationModeState(key as PublicRegistrationMode)}
                                items={[
                                    {key: "single", label: labels.individualRegistrationTab},
                                    {key: "bulk", label: labels.bulkRegistrationTab}
                                ]}
                            />
                        </nav>

                        <Form
                            form={registrationForm}
                            layout="vertical"
                            onFinish={registrationMode === "single" ? submitRegistration : undefined}
                            onFinishFailed={registrationMode === "single" ? handleRegistrationFinishFailed : undefined}
                            onValuesChange={(_, allValues) => {
                                if (submitError) {
                                    setSubmitError(null);
                                }
                                if (bulkValidationNotice) {
                                    setBulkValidationNotice(null);
                                }
                                if (bulkSubmissionResults.length > 0) {
                                    setBulkSubmissionResults([]);
                                }
                                saveCurrentDraft(allValues, registrationMode);
                            }}
                            className="public-registration-form"
                            requiredMark="optional"
                        >
                            <Form.Item name="participantTypeProofFileId" hidden>
                                <Input type="hidden"/>
                            </Form.Item>
                            <Form.Item name="participantTypeProofFileName" hidden>
                                <Input type="hidden"/>
                            </Form.Item>
                            {registrationMode === "bulk" ? (
                                <BulkRegistrationSection
                                    eventForm={form}
                                    payerType={payerType}
                                    requiresParticipationFee={requiresParticipationFee}
                                    selectedParticipationFee={selectedParticipationFee}
                                    showParticipationPayment={hasParticipationOfferItems}
                                    participationMethods={participationMethods}
                                    rows={watchedValues?.bulkRows}
                                    importNotice={bulkImportNotice}
                                    onImportCsv={file => void importBulkRegistrationCsvFile(file)}
                                    validationNotice={bulkValidationNotice}
                                    submissionResults={bulkSubmissionResults}
                                    submitErrorAlert={submitErrorAlert}
                                    submitting={submitting}
                                    onValidate={() => void validateBulkRegistration()}
                                    onSubmit={() => void submitBulkRegistration()}
                                />
                            ) : (
                                <SingleRegistrationForm
                                    eventForm={form}
                                    payerType={payerType}
                                    requiresParticipationFee={requiresParticipationFee}
                                    registrationOptions={availableRegistrationOptions}
                                    showProofField={selectedParticipationFeeRequiresProof}
                                    proofFileId={watchedValues?.participantTypeProofFileId}
                                    proofFileName={watchedValues?.participantTypeProofFileName}
                                    proofUploading={participantTypeProofUploading}
                                    onProofUpload={file => void uploadParticipantTypeProof(file)}
                                    showParticipationPayment={hasParticipationOfferItems}
                                    participationMethods={participationMethods}
                                    reserveAccommodation={reserveAccommodation}
                                    selectedRoom={selectedRoom}
                                    accommodationEstimate={accommodationEstimate}
                                    accommodationMethods={accommodationMethods}
                                    submitting={submitting}
                                    submitErrorAlert={submitErrorAlert}
                                />
                            )}
                        </Form>
                    </>
                )}
            </>
        );
    };

    return (
        <ConfigProvider theme={publicTheme.theme}>
            <RegistrationCopyProvider value={copy}>
                <PublicShell
                    embedded={embedded}
                    mainRef={pageRef}
                    style={publicTheme.style}
                    busy={loading || submitting}
                    header={<RegistrationBrandLockup eventName={publicEventName} logoUrl={trimValue(eventForm?.logoUrl)}/>}
                    toolbar={<LanguageSwitch value={language} onChange={onLanguageChange} label={labels.language}/>}
                >
                    {loading && !eventForm ? (
                        <div className="public-registration-status">
                            <Skeleton active paragraph={{rows: 6}} title={{width: "60%"}}/>
                            <Typography.Text>{labels.loading}</Typography.Text>
                        </div>
                    ) : null}

                    {error ? <Alert type="error" showIcon message={error}/> : null}

                    {eventForm && !error ? renderRegistration(eventForm) : null}
                </PublicShell>
            </RegistrationCopyProvider>
        </ConfigProvider>
    );
};

export default PublicRegistrationPage;
